import Text from '@deprecated-ui/common/Text';
import colors from '@deprecated-ui/_style/color';
import Icon from '@ant-design/icons';
import InfoSvg from 'global/Securities/components/Icons/InfoSvg';
import { lotSize } from 'global/TradingModal/constant';
import { isRedirectToNewCore } from 'features/securities/utils';

import { TransactionHistoryListItem } from 'global/Securities/types/api/history';
import { BondInfoType } from 'global/TradingModal/RealTradingModal/ModalBonds/constants';
import {
  formatCurrency,
  formatBondCurrency,
} from 'features/securities/withdrawal/utils';

import { Flex } from 'features/securities/styled';
import Image from 'global/Image';
import alertMessage from 'global/AlertMessage';
import {
  formatDate,
  formatPrice,
  formatPriceRoundDown,
  getRealizedPnLText,
} from './utils';
import {
  HistoryType,
  COUPONS,
  TransferCash,
  TransferStock,
  ShowTransferStatus,
} from './types';

export const getDetailTableData = (
  dataTable: TransactionHistoryListItem,
  onClickFee = () => {},
  openInfoModal = (info: BondInfoType) => {},
) => {
  let dataSource = [];
  const configStatusDepositWithdrawal = [
    {
      key: '1',
      color: colors.highgray,
      status: 'PENDING',
    },
    {
      key: '2',
      color: colors.primary,
      status: 'SUCCESS',
    },
    {
      key: '6',
      color: colors.sunsetOrange,
      status: 'FAILED',
    },
  ];

  const config = {
    gain: {
      title: 'Realized Gain',
      color: colors.primary,
    },
    loss: {
      title: 'Realized Loss',
      color: colors.sunsetOrange,
    },
  };

  const generateRealizedValueConfig = (value: number) => {
    if (value === 0) {
      return {
        title: 'Realized Gain',
        color: '#333333',
      };
    }
    return value < 0 ? config.loss : config.gain;
  };

  const generateItem = (label: string, value: string) => ({
    label: <Text as="span">{label}</Text>,
    value: (
      <Text as="div" align="right">
        {value}
      </Text>
    ),
  });

  const generateDateItem = () => ({
    label: <Text as="span">Date</Text>,
    value: (
      <Text as="div" align="right">
        {formatDate(dataTable.date)}
      </Text>
    ),
  });

  const generatePriceItem = (title: string, roundDown = false) => {
    const price = roundDown
      ? formatPriceRoundDown(dataTable.price)
      : formatPrice(dataTable.price);

    return {
      label: <Text as="span">{title || 'Price'}</Text>,
      value: (
        <Text as="div" align="right">
          {price}
        </Text>
      ),
    };
  };

  const generateLotItem = (title: string) => ({
    label: <Text as="span">{title || 'Lot'}</Text>,
    value: (
      <Text as="div" align="right">
        {formatPrice(dataTable.lot)}
      </Text>
    ),
  });

  const generateAmountItem = (label: string, netAmount = true) => {
    const amount = netAmount ? dataTable.netamount : dataTable.amount;
    return {
      label: (
        <Text as="span" family="bold">
          {label}
        </Text>
      ),
      value: (
        <Text as="div" align="right" family="bold">
          {formatPrice(amount)}
        </Text>
      ),
    };
  };

  const generateBuySellSource = () => [
    generateDateItem(),
    generatePriceItem('Price'),
    generateLotItem('Lot Done'),
    {
      label: <Text as="span">Amount</Text>,
      value: (
        <Text as="div" align="right">
          {formatPrice(dataTable.amount)}
        </Text>
      ),
    },
    {
      label: <Text as="span">Total Fee</Text>,
      value: (
        <Text as="div" align="right">
          {formatPrice(dataTable.fee)}
        </Text>
      ),
    },
    generateAmountItem('Net Amount'),
  ];

  const generateStatus = (statusText: string) => {
    const configStatusActive = configStatusDepositWithdrawal.find(
      (e) => e.status === dataTable.status.toUpperCase(),
    );

    return {
      label: <Text as="span">Status</Text>,
      value: (
        <Text
          as="div"
          align="right"
          family="bold"
          color={configStatusActive.color}
        >
          {statusText}
        </Text>
      ),
    };
  };

  const generateWithdrawMutualFund = () => {
    const configStatusActive = configStatusDepositWithdrawal.find(
      (e) => e.status === dataTable.status.toUpperCase(),
    );

    return [
      {
        label: <Text as="span">Status</Text>,
        value: (
          <Text
            as="div"
            align="right"
            family="bold"
            color={configStatusActive.color}
          >
            {dataTable.status}
          </Text>
        ),
      },
      generateDateItem(),
      {
        label: <Text as="span">Destination</Text>,
        value: (
          <Text as="div" align="right">
            Bibit
          </Text>
        ),
      },
    ];
  };

  if (dataTable.command === HistoryType.BUY) {
    dataSource = generateBuySellSource();
  }

  if (
    [HistoryType.SELL, HistoryType.FORCED_SELL_DAY_TRADE].includes(
      dataTable.command,
    )
  ) {
    const { color, title } = generateRealizedValueConfig(
      dataTable.realized_amount,
    );

    dataSource = generateBuySellSource();

    if (!dataTable.is_realized_gain_hidden) {
      dataSource.push({
        label: (
          <Text as="span" family="bold">
            {title}
          </Text>
        ),
        value: (
          <Text as="div" align="right" family="bold" color={color}>
            {getRealizedPnLText(dataTable, 2)}
          </Text>
        ),
      });
    }
  }

  if (
    [
      HistoryType.DEPOSIT,
      HistoryType.CASHBACK_STOCKBIT,
      HistoryType.CASHBACK_BROKER_FEE,
    ].includes(dataTable.command)
  ) {
    const configStatusActive = configStatusDepositWithdrawal.find(
      (e) => e.status === dataTable.status.toUpperCase(),
    );
    dataSource = [
      {
        label: <Text as="span">Status</Text>,
        value: (
          <Text
            as="div"
            align="right"
            family="bold"
            color={configStatusActive.color}
          >
            {dataTable.status}
          </Text>
        ),
      },
      generateDateItem(),
      generateAmountItem('Deposit Amount'),
    ];

    // Need to hide empty account number due to manual deposit
    // and if account number is 0000000000 to handle deposit to RDN BCA
    // with other banks
    if (
      dataTable.bank_account_number !== '' &&
      dataTable.bank_account_number !== '0000000000'
    ) {
      dataSource.splice(2, 0, {
        label: <Text as="span">Account Number</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.bank_account_number}
          </Text>
        ),
      });
    }
  }

  const isMutualFund = !!dataTable?.partner_reference;
  const isBasketBuyMutualFund = isMutualFund && dataTable?.mutual_fund === '';

  if (dataTable.command === HistoryType.WITHDRAW) {
    const configStatusActive = configStatusDepositWithdrawal.find(
      (e) => e.status === dataTable.status.toUpperCase(),
    );

    const transferFee =
      dataTable?.fee && !dataTable?.is_hide_transfer_fee ? (
        `-${formatPrice(dataTable?.fee)}`
      ) : (
        <span className="free-transfer">Gratis</span>
      );

    const fontFamily =
      !isRedirectToNewCore() || dataTable?.is_hide_transfer_fee ? 'bold' : null;

    dataSource = [
      {
        label: <Text as="span">Status</Text>,
        value: (
          <Text
            as="div"
            align="right"
            family="bold"
            color={configStatusActive.color}
          >
            {dataTable.status}
          </Text>
        ),
      },
      generateDateItem(),
      {
        ...(dataTable?.symbol
          ? {
              label: <Text as="span">Transfer Method</Text>,
              value: (
                <Text as="div" align="right">
                  {dataTable?.symbol}
                </Text>
              ),
            }
          : {}),
      },
      {
        label: <Text as="span">Destination Bank</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.bank_name}
          </Text>
        ),
      },
      {
        label: <Text as="span">Account Number</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.bank_account_number}
          </Text>
        ),
      },
      {
        label: (
          <Text as="span" family={fontFamily}>
            Withdrawal Amount
          </Text>
        ),
        value: (
          <Text as="div" align="right" family={fontFamily}>
            {formatPrice(dataTable.netamount)}
          </Text>
        ),
      },
      {
        ...(isRedirectToNewCore() && !dataTable?.is_hide_transfer_fee
          ? {
              label: <Text as="span">Transfer Fee</Text>,
              value: (
                <Text as="div" align="right">
                  {transferFee}
                </Text>
              ),
            }
          : {}),
      },
      {
        ...(isRedirectToNewCore()
          ? !dataTable?.is_hide_transfer_fee && {
              label: (
                <Text as="span" family="bold">
                  Received Amount
                </Text>
              ),
              value: (
                <Text as="div" align="right" family="bold">
                  {formatPrice(dataTable?.amount)}
                </Text>
              ),
            }
          : {}),
      },
    ];
  }

  if (dataTable.command === HistoryType.WITHDRAW && isMutualFund) {
    dataSource = [
      generateWithdrawMutualFund(),
      {
        label: <Text as="span">Mutual Fund</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.mutual_fund}
          </Text>
        ),
      },
      {
        label: <Text as="span">Transaction ID</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.partner_reference}
          </Text>
        ),
      },
      generateAmountItem('Withdrawal Amount'),
    ];
  }

  if (dataTable.command === HistoryType.WITHDRAW && isBasketBuyMutualFund) {
    dataSource = [
      generateWithdrawMutualFund(),
      {
        label: <Text as="span">Transaction ID</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.partner_reference}
          </Text>
        ),
      },
      generateAmountItem('Withdrawal Amount'),
    ];
  }

  if (dataTable.command === HistoryType.EXERCISE) {
    dataSource = [
      generateDateItem(),
      generatePriceItem('Exercise Price'),
      {
        label: <Text as="span">Shares</Text>,
        value: (
          <Text as="div" align="right">
            {formatPrice(dataTable.lot * lotSize)}
          </Text>
        ),
      },
      generateAmountItem('Exercise Amount'),
    ];
  }

  if (dataTable.command === HistoryType.DIVIDEND) {
    dataSource = [
      generateDateItem(),
      {
        label: <Text as="span">Type</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.dividend_type}
          </Text>
        ),
      },
      {
        label: <Text as="span">Shares</Text>,
        value: (
          <Text as="div" align="right">
            {formatPrice(dataTable.shares)}
          </Text>
        ),
      },
    ];

    if (dataTable.dividend_type.toUpperCase() === 'CASH') {
      dataSource.splice(
        3,
        0,
        {
          label: <Text as="span">Dividend/Share</Text>,
          value: (
            <Text as="div" align="right">
              {dataTable.dividend_per_share}
            </Text>
          ),
        },
        generateAmountItem('Dividend Amount (Rp)'),
      );
    }

    if (dataTable.dividend_type.toUpperCase() === 'STOCK') {
      dataSource.splice(
        3,
        0,
        {
          label: <Text as="span">Ratio (Old:New)</Text>,
          value: (
            <Text as="div" align="right">
              {dataTable.dividend_ratio}
            </Text>
          ),
        },
        generateAmountItem('Dividend Amount (Shares)'),
      );
    }
  }

  if (dataTable.command === HistoryType.IPO) {
    dataSource = [
      generateDateItem(),
      generateLotItem('Lot'),
      generatePriceItem('Price'),
      generateAmountItem('Total IPO Amount'),
    ];
  }

  if (dataTable.command === HistoryType.REFUND) {
    dataSource = [
      generateDateItem(),
      generateLotItem('Lot'),
      generatePriceItem('Price'),
      generateAmountItem('Refund Amount'),
    ];
  }

  if (dataTable.command === HistoryType.METERAI) {
    dataSource = [generateDateItem(), generateAmountItem('Biaya Meterai')];
  }

  if (dataTable.command === HistoryType.STAMP_DUTY_WITHDRAW) {
    dataSource = [
      generateDateItem(),
      {
        label: <Text as="span">Type</Text>,
        value: (
          <Text as="div" align="right">
            Bea Materai Bibit
          </Text>
        ),
      },
      generateAmountItem('Withdrawal Amount'),
    ];
  }

  if (dataTable.command === HistoryType.SBN_WITHDRAW) {
    const configStatusActive = configStatusDepositWithdrawal.find(
      (e) => e.status === dataTable.status.toUpperCase(),
    );
    dataSource = [
      {
        label: <Text as="span">Status</Text>,
        value: (
          <Text
            as="div"
            align="right"
            family="bold"
            color={configStatusActive.color}
          >
            {dataTable.status}
          </Text>
        ),
      },
      generateDateItem(),
      {
        label: <Text as="span">Destination</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.bank_name}
          </Text>
        ),
      },
      {
        label: <Text as="span">Type</Text>,
        value: (
          <Text as="div" align="right">
            SBN
          </Text>
        ),
      },
      {
        label: <Text as="span">Transaction ID</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.partner_reference}
          </Text>
        ),
      },
      generateAmountItem('Withdrawal Amount'),
    ];
  }

  if (dataTable?.command === HistoryType.CONVERSION) {
    dataSource = [
      {
        label: <span>Date</span>,
        value: <span>{formatDate(dataTable?.date)}</span>,
      },
      {
        label: <span>Conversion Price</span>,
        value: <span>{dataTable?.price}</span>,
      },
      {
        label: <span>Shares</span>,
        value: <span>{dataTable?.shares}</span>,
      },
      {
        label: <b>Conversion Amount</b>,
        value: <b>{formatPrice(dataTable?.amount)}</b>,
      },
    ];
  }

  if (
    dataTable.command === HistoryType.BUY_FR ||
    dataTable.command === HistoryType.REFUND_FR
  ) {
    dataSource = [
      {
        label: <span>Tanggal Pembelian</span>,
        value: <span>{formatDate(dataTable.date)}</span>,
      },
      {
        label: <span>Unit Pembelian</span>,
        value: <span>{dataTable.shares}</span>,
      },
      {
        label: <span>Harga Beli (%)</span>,
        value: <span>{dataTable?.bonds?.bonds_price}</span>,
      },
      {
        label: <span>Nilai Pembelian</span>,
        value: <span>{formatBondCurrency(dataTable.amount)}</span>,
      },
      {
        label: <span>Kupon Penjual</span>,
        value: (
          <span>
            {formatBondCurrency(
              dataTable.bonds?.accrued_interest,
              'exceptZero',
            )}
          </span>
        ),
      },
      {
        label: <span>Bea Meterai</span>,
        value: (
          <span>
            {formatBondCurrency(dataTable?.bonds?.stamp_duty, 'exceptZero')}
          </span>
        ),
      },
      {
        label: <b>Nominal Pembelian</b>,
        value: <b>{formatBondCurrency(dataTable.netamount)}</b>,
      },
    ];
  }

  if (dataTable.command === HistoryType.SELL_FR) {
    dataSource = [
      {
        label: <span>Tanggal Penjualan</span>,
        value: <span>{formatDate(dataTable.date)}</span>,
      },
      {
        label: <span>Unit Penjualan</span>,
        value: <span>{dataTable.shares}</span>,
      },
      {
        label: <span>Harga Jual (%)</span>,
        value: <span>{dataTable.bonds?.bonds_price}</span>,
      },
      {
        label: <span>Nilai Penjualan</span>,
        value: <span>{formatBondCurrency(dataTable.amount)}</span>,
      },
      {
        label: <span>Kupon Berjalan</span>,
        value: (
          <span>
            {formatBondCurrency(
              dataTable?.bonds?.accrued_interest_exclude_tax,
              'exceptZero',
            )}
          </span>
        ),
      },
      {
        label: (
          <Flex align="center">
            <span>Pajak Obligasi&nbsp;</span>
            <Icon
              component={InfoSvg}
              style={{ color: '#969696' }}
              onClick={() => openInfoModal(BondInfoType.BOND_TAX)}
            />
          </Flex>
        ),
        value: (
          <span>{formatBondCurrency(dataTable.fee * -1, 'exceptZero')}</span>
        ),
      },
      {
        label: <span>Bea Meterai</span>,
        value: (
          <span>
            {formatBondCurrency(
              dataTable?.bonds?.stamp_duty * -1,
              'exceptZero',
            )}
          </span>
        ),
      },
      Math.abs(dataTable.bonds.seller_coupon) > 0
        ? {
            label: (
              <Flex align="center" style={{ gap: 2 }}>
                <Flex align="center">
                  <span>Kupon Penjual&nbsp;</span>
                  <Icon
                    component={InfoSvg}
                    style={{ color: '#969696' }}
                    onClick={() =>
                      openInfoModal(BondInfoType.ACCRUED_INTEREST_SELLER)
                    }
                  />
                </Flex>
              </Flex>
            ),
            value: (
              <span>
                {formatBondCurrency(
                  dataTable.bonds.seller_coupon,
                  'exceptZero',
                )}
              </span>
            ),
          }
        : {},
      {
        label: <span>Nominal Diterima</span>,
        value: <span>{formatBondCurrency(dataTable.netamount)}</span>,
      },
    ];
  }

  if (COUPONS.includes(dataTable.command)) {
    dataSource = [
      {
        label: <span>Tanggal Pencairan Kupon</span>,
        value: <span>{formatDate(dataTable.date)}</span>,
      },
      {
        label: <span>Rate Kupon</span>,
        value: <span>{dataTable.bonds?.bonds_price}</span>,
      },
      {
        label: <span>Total Unit</span>,
        value: <span>{dataTable.shares}</span>,
      },
      {
        label: <span>Nominal Kupon</span>,
        value: <span>{formatCurrency(String(dataTable.amount))}</span>,
      },
      {
        label: <span>Pajak</span>,
        value: (
          <span>
            {dataTable.fee !== 0 && '-'}
            {formatCurrency(String(dataTable.fee))}
          </span>
        ),
      },
      dataTable.bonds.seller_coupon > 0 && {
        label: (
          <Flex align="center" style={{ gap: 2 }}>
            <Flex align="center">
              <span>Kupon Penjual</span>
              <Icon
                component={InfoSvg}
                style={{ color: '#969696' }}
                onClick={() =>
                  openInfoModal(BondInfoType.ACCRUED_INTEREST_SELLER)
                }
              />
            </Flex>
          </Flex>
        ),
        value: (
          <span>{formatCurrency(String(dataTable.bonds.seller_coupon))}</span>
        ),
      },
      {
        label: <b>Kupon Diterima (Nett)</b>,
        value: <b>{formatCurrency(String(dataTable.netamount))}</b>,
      },
    ];
  }

  if (dataTable.command === HistoryType.DATA_FEE) {
    dataSource = [generateDateItem(), generateAmountItem('Biaya Datafeed')];
  }

  if (
    dataTable.command === HistoryType.SBN_MATURITY_REDEMPTION ||
    dataTable.command === HistoryType.SBN_EARLY_REDEMPTION
  ) {
    dataSource = [
      generateDateItem(),
      {
        label: (
          <Text as="span" family="bold">
            {dataTable.command === HistoryType.SBN_MATURITY_REDEMPTION
              ? 'Bond Redemption Amount'
              : 'Early Redemption Amount'}
          </Text>
        ),
        value: (
          <Text as="div" align="right" family="bold">
            {formatPrice(dataTable.netamount)}
          </Text>
        ),
      },
    ];
  }

  if (
    dataTable.command === HistoryType.FR_MATURITY_REDEMPTION ||
    dataTable.command === HistoryType.BOND_REDEMPTION
  ) {
    const { color: realizedColor, title: realizedTitle } =
      generateRealizedValueConfig(dataTable.realized_amount);

    dataSource = [
      {
        label: <Text as="span">Maturity Date</Text>,
        value: (
          <Text as="div" align="right">
            {formatDate(dataTable.date)}
          </Text>
        ),
      },
      {
        label: <Text as="span">Sell Amount (Rp)</Text>,
        value: (
          <Text as="div" align="right">
            {formatPrice(dataTable.amount)}
          </Text>
        ),
      },
      {
        label: <Text as="span">Sell Price (%)</Text>,
        value: (
          <Text as="div" align="right">
            100.00%
          </Text>
        ),
      },
      {
        label: <Text as="span">Avg. Buy Price (%)</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.price} %
          </Text>
        ),
      },
      {
        label: (
          <Text as="span">
            Pajak Keuntungan
            <Text as="span" style={{ cursor: 'pointer' }} onClick={onClickFee}>
              <Icon
                component={InfoSvg}
                style={{
                  fontSize: 16,
                  color: '#969696',
                  marginLeft: 4,
                }}
              />
            </Text>
          </Text>
        ),
        value: (
          <Text as="div" align="right">
            {formatPrice(dataTable.fee, false)}
          </Text>
        ),
      },
      generateAmountItem('Total Amount'),
      {
        label: (
          <Text as="span" family="bold">
            {realizedTitle}
          </Text>
        ),
        value: (
          <Text as="div" align="right" family="bold" color={realizedColor}>
            {getRealizedPnLText(dataTable, 2)}
          </Text>
        ),
      },
    ];
  }

  if (dataTable.command === HistoryType.IFA_WITHDRAW) {
    const configStatusActive = configStatusDepositWithdrawal.find(
      (e) => e.status === dataTable.status.toUpperCase(),
    );

    dataSource = [
      {
        label: <Text as="span">Status</Text>,
        value: (
          <Text
            as="div"
            align="right"
            family="bold"
            color={configStatusActive.color}
          >
            {dataTable.status}
          </Text>
        ),
      },
      {
        label: <Text as="span">Transaction ID</Text>,
        value: (
          <Text
            as="div"
            align="right"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {dataTable.label}
            <Image
              width="14"
              height="13"
              src="icon/copy-transaction-id.svg"
              style={{ marginLeft: 4, cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(dataTable.label);
                alertMessage({
                  content: 'Nomor Transaction ID berhasil disalin',
                  alertType: 'snackbar',
                  messageType: 'success',
                  hasCloseIcon: true,
                });
              }}
            />
          </Text>
        ),
      },
      generateDateItem(),
      {
        label: <Text as="span">Type</Text>,
        value: (
          <Text as="div" align="right">
            Advisory Fee
          </Text>
        ),
      },
      {
        label: <Text as="span">Destination</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.bank_name}
          </Text>
        ),
      },
      generateAmountItem('Withdrawal Amount'),
    ];
  }

  if (TransferCash.includes(dataTable.command)) {
    dataSource = [
      generateDateItem(),
      generateItem('Source', dataTable.move_stock_cash?.source?.name),
      generateItem('Destination', dataTable.move_stock_cash?.destination?.name),
      generateAmountItem('Transfer Amount'),
    ];

    if (ShowTransferStatus.includes(dataTable.status)) {
      dataSource.unshift(generateStatus(dataTable.status.toUpperCase()));
    }
  }

  if (TransferStock.includes(dataTable.command)) {
    dataSource = [
      generateDateItem(),
      generateLotItem('Lot'),
      generatePriceItem('Average Price', true),
      generateItem('Source', dataTable.move_stock_cash?.source?.name),
      generateItem('Destination', dataTable.move_stock_cash?.destination?.name),
      generateAmountItem('Amount', false), // use amount instead of netamount
    ];

    if (ShowTransferStatus.includes(dataTable.status)) {
      dataSource.unshift(generateStatus(dataTable.status.toUpperCase()));
    }
  }

  if (dataTable.command === HistoryType.CAPITAL_REDUCTION) {
    dataSource = [
      {
        label: <Text as="span">Date</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.date}
          </Text>
        ),
      },
      {
        label: (
          <Text as="span" family="bold">
            Capital Reduction Amount (Rp)
          </Text>
        ),
        value: (
          <Text as="div" align="right" family="bold">
            {formatPrice(dataTable.netamount)}
          </Text>
        ),
      },
    ];
  }

  if (
    dataTable.command === HistoryType.SWEEP_IN ||
    dataTable.command === HistoryType.SWEEP_OUT
  ) {
    const amountLabel =
      dataTable.command === HistoryType.SWEEP_IN
        ? 'Sweep In Amount'
        : 'Sweep Out Amount';
    dataSource = [
      {
        label: <Text as="span">Date</Text>,
        value: (
          <Text as="div" align="right">
            {dataTable.date}
          </Text>
        ),
      },
      {
        label: (
          <Text as="span" family="bold">
            {amountLabel}
          </Text>
        ),
        value: (
          <Text as="div" align="right" family="bold">
            {formatPrice(dataTable.netamount)}
          </Text>
        ),
      },
    ];
  }

  return dataSource;
};

export default getDetailTableData;
